import CardView from "../../../../../components/common/CardView";
import { findObjectUseCase } from "../../../../../usecases/object";
import BaseFormPage from "../../../../../base/BaseFormPage";
import { FloatingForm } from "../../../../../components/common/Converter";
import InputFactory from "../../../../../components/InputFactory";
import LeaveRequestFormPresenter from "./LeaveRequestFormPresenter";
import FormFactory from "../../../../../components/FormFactory";
import SearchSelect from "../../../../../components/searchSelect/SearchSelect";

const REHAB_LEAVES = [
  "Rehabilitation Leave",
  "Special Emergency Leave",
  "Special Leave Privilege",
  "Maternity Leave",
  "Special Leave Benefit",
];

const MALE_EXCLUDED = ["Special Leave Benefit", "Maternity Leave"];
const FEMALE_EXCLUDED = ["Paternity Leave"];

export default class LeaveRequestForm extends BaseFormPage {
  constructor(props) {
    super(props);
    this.presenter = new LeaveRequestFormPresenter(this);
    this.state = {
      object: {},
      selectedFiles: [],
      employee: [],
      allLeave: [],
      dateDuration: 0,
      startdate: "",
      enddate: "",
      isRehab: false,
      isWellness: false,
      maxEndDate: null,
      leaves: [],
    };
  }

  async componentDidMount() {
    const employeeID = this.getCurrentUser().employee.id;
    const [employee, leaves, leaveType] = await Promise.all([
      findObjectUseCase().execute("employees", { where: { id: employeeID } }),
      findObjectUseCase().execute("leave_cred", {
        where: { empID: employeeID },
      }),
      findObjectUseCase().execute("leave_type", {
        where: { status: "active" },
      }),
    ]);

    const gender = employee[0].sex;
    const excluded = gender === "Male" ? MALE_EXCLUDED : FEMALE_EXCLUDED;
    const allLeave = leaveType.filter((l) => !excluded.includes(l.name));

    this.setState({ employee, allLeave, leaves });

    if (Object.keys(this.props.object).length > 0) {
      const { object } = this.props;
      this.setState({
        object,
        selectedFiles: object.selectedFiles,
        startdate: object.startdate
          ? new Date(object.startdate).toISOString().split("T")[0]
          : "",
        enddate: object.enddate
          ? new Date(object.enddate).toISOString().split("T")[0]
          : undefined,
      });
    }
  }

  getCollectionName() {
    return "leave_request";
  }

  closeTrainingForm() {
    return this.props.closeTrainingForm();
  }

  async refetchData() {
    return await this.props.refetchData();
  }

  getMinSelectableDate(daysFromToday) {
    const date = new Date();
    date.setDate(date.getDate() + daysFromToday);
    return date.toISOString().split("T")[0];
  }

  async onChange(value, field) {
    if (field === "leaveType") {
      const [leave] = await findObjectUseCase().execute("leave_type", {
        where: { id: value.id },
      });
      const isRehab = REHAB_LEAVES.includes(value.name);
      const isWellness = value.name === "Wellness Leave";
      this.setState({
        isRehab,
        isWellness,
        startdate: isRehab ? "" : this.state.startdate,
        enddate: isRehab ? "" : this.state.enddate,
        maxEndDate: null,
        dateDuration: leave?.duration || 0,
      });
    }
    this.setState((prev) => ({ object: { ...prev.object, [field]: value } }));
  }

  onDateChange(key, value, object) {
    if (key === "startdate" && this.state.isWellness && value) {
      const maxDate = new Date(value);
      maxDate.setDate(maxDate.getDate() + 2);
      this.setState({ maxEndDate: maxDate.toISOString().split("T")[0] });
    }
    this.presenter.onChangeForm(key, value, object);
  }

  renderDateField(label) {
    const key = label.replace(/\s+/g, "").toLowerCase();
    const { object, dateDuration, isRehab, isWellness, maxEndDate } =
      this.state;
    const isViewData = Object.keys(this.props.object).length > 0;
    const isEndDate = label === "End Date";

    return (
      <div key={label} className="col-lg-6">
        <label className="mb-2 fs-sm">
          {label} <span className="text-danger">*</span>
        </label>
        <InputFactory
          type="Date"
          required={isEndDate && object.halfDay ? false : true}
          disabled={isViewData || (isEndDate && object.halfDay)}
          min={this.getMinSelectableDate(dateDuration || 0)}
          max={isEndDate && (isRehab || isWellness) ? maxEndDate : null}
          value={this.state[key]}
          onChange={(value) => this.onDateChange(key, value, object)}
        />
      </div>
    );
  }

  getSelectedDateDuration(object) {
    if (!object?.startdate) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = new Date(object.startdate);
    startDate.setHours(0, 0, 0, 0);

    return Math.ceil((startDate - today) / (1000 * 60 * 60 * 24)) + 1;
  }

  render() {
    const isViewData = Object.keys(this.props.object).length > 0;
    const schema = this.getSchema(this.getCollectionName());
    const { object, allLeave, selectedFiles } = this.state;

    const exclude = (keys) =>
      Object.keys(schema.fields).filter((k) => !keys.includes(k));

    const normalize = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const leaveType = normalize(object?.leaveType?.name);

    const selectedDateDuration = this.getSelectedDateDuration(this.state);

    const leaveSpanDuration =
      this.state.startdate && this.state.enddate
        ? Math.ceil(
            (new Date(this.state.enddate) - new Date(this.state.startdate)) /
              (1000 * 60 * 60 * 24),
          ) + 1
        : 0;

    const attachementRequired =
      object?.abroad ||
      [
        "maternityleave",
        "adoptionleave",
        "paternityleave",
        "specialprivilegeleave",
        "specialleaveprivilege",
        "soloparent",
        "soloparentleave",
        "specialemergencycalamityleave",
        "vawcleave",
        "rehabilitationleave",
        "specialleavebenefitsforwomen",
        "specialemergencycalamity",
        "terminalleave",
        "adoptionleave",
      ].includes(leaveType) ||
      (selectedDateDuration >= 4 && leaveType === "sickleave");

    const uploadRequired = [
      ...(selectedDateDuration > 30
        ? ["DTR", "Leave form", "Certification", "No Pending Backlogs", "ITR"]
        : [
            "DTR",
            "Leave form",
            "Certification",
            "No Pending Backlogs",
            "ITR",
            "Justification",
          ]),
      ...(leaveSpanDuration >= 30 ? ["Clearance"] : []),
    ];
    return (
      <FloatingForm width="30rem">
        <CardView style={{ padding: 0 }}>
          <h4 className="fs-6 fw-bold pt-3 ps-3">Leave Request Details</h4>
          <hr />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              this.presenter.hanldeSubmit();
            }}
          >
            <div className="w-100" style={{ padding: "0 1.5rem" }}>
              <div className="p-1">
                <label className="mb-2 fs-sm">
                  Leave Type <span className="text-danger">*</span>
                </label>
                <SearchSelect
                  value={
                    isViewData ? object.selectedType : object?.leaveType?.name
                  }
                  focus={true}
                  disabled={isViewData}
                  required
                  onChange={(value) => this.onChange(value, "leaveType")}
                  options={allLeave.map((l) => ({
                    label: l.name,
                    value: { name: l.name, id: l.id },
                  }))}
                />
              </div>

              <FormFactory
                className="p-1"
                schema={schema}
                object={object}
                disabled={isViewData}
                onChange={this.onChange.bind(this)}
                excludeFields={exclude(["halfDay", "reason"])}
              />

              <div className="row mt-3">
                {["Start Date", "End Date"].map((label) =>
                  this.renderDateField(label),
                )}

                <p className="mt-4">In case of Vacation Leave:</p>
                <FormFactory
                  className="p-1"
                  schema={schema}
                  object={object}
                  disabled={isViewData}
                  onChange={this.onChange.bind(this)}
                  excludeFields={exclude([
                    "philippines",
                    "abroad",
                    "abroadspecify",
                  ])}
                />

                <p className="mt-4">In case of Sick Leave:</p>
                <FormFactory
                  className="p-1"
                  schema={schema}
                  object={object}
                  disabled={isViewData}
                  onChange={this.onChange.bind(this)}
                  excludeFields={exclude([
                    "hospital",
                    "inhospital",
                    "patient",
                    "outpatient",
                    "workingDays",
                    ...[isViewData ? "complianceRemarks" : ""],
                  ])}
                />

                <div className="mb-4 mt-2">
                  <label className="mb-2 fs-6">
                    Attachments{" "}
                    {attachementRequired && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                  <InputFactory
                    type="File"
                    onChange={(val) =>
                      this.presenter.onChangeForm("selectedFiles", val)
                    }
                    required={attachementRequired}
                    disabled={isViewData && object.stats !== "For Compliance"}
                    value={selectedFiles}
                    className="form-control"
                  />
                </div>
                {/* notes */}
                <div>
                  Required files to be submitted:
                  <br />
                  {object?.abroad &&
                    uploadRequired.map((item, index) => (
                      <div>
                        {index + 1}. {item}
                      </div>
                    ))}
                </div>
                {object?.abroad && selectedDateDuration <= 30 && (
                  <div className="mt-2">
                    <p className="mb-1 fst-italic">
                      If there are pending cases, upload affidavit of
                      undertaking.
                    </p>
                    <p className="mb-0 fw-bold">
                      Please submit the original and hard-copy documents to the
                      Human Resource Management Section (HRMS) office for DOJ
                      compliance.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <hr />
            <div className="w-100 d-flex justify-content-end gap-4 mb-3 pe-3">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => this.props.closeTrainingForm()}
              >
                Cancel
              </button>
              {!isViewData && (
                <button className="btn btn-primary" type="submit">
                  Submit
                </button>
              )}
              {object.stats === "For Compliance" && (
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() =>
                    this.presenter.handleUpdateCompliance(
                      object,
                      "Recommending for Approval",
                    )
                  }
                >
                  Update
                </button>
              )}
            </div>
          </form>
        </CardView>
      </FloatingForm>
    );
  }
}
